import {
  KitButton,
  KitInput,
  KitModal,
  KitSnack,
  KitText,
} from '@omni/kit/components';
import { KitSnackDuration } from '@omni/kit/components/KitSnack.types';
import { useTranslation } from '@omni/kit/hooks/useTranslation';
import Spacing from '@omni/kit/theming/Spacing';
import React, {
  ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Platform, StyleSheet, TextInput, View } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export interface ChangePasswordModalProps {
  clientBrandColor?: string;
  saveNewPassword: (
    oldPass: string,
    newPass: string,
    confirmPass: string
  ) => Promise<void>;
  setVisible: (visible: boolean) => void;
  visible: boolean;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  clientBrandColor,
  visible,
  setVisible,
  saveNewPassword,
}): ReactElement => {
  const { t } = useTranslation(['blocks']);
  const { bottom: insetsBottom } = useSafeAreaInsets();

  const oldPasswordRef = useRef<TextInput>(null);

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // ---------------------------------------------------------------------------
  // Computed State
  const isValidPassword = useMemo((): boolean => {
    /** Password must contain at least eight characters */
    const passwordRegex = /^.{8,}$/g;

    return Boolean(
      newPassword.match(passwordRegex) &&
        confirmPassword.match(passwordRegex) &&
        oldPassword !== '' &&
        newPassword === confirmPassword
    );
  }, [newPassword, confirmPassword, oldPassword]);

  const handleSave = async (): Promise<void> => {
    try {
      if (isValidPassword) {
        await saveNewPassword(oldPassword, newPassword, confirmPassword);
        handlePasswordUpdated(true);
      } else {
        KitSnack.show(t('blocks:changePassword.passwordMatch'));
      }
    } catch (e) {
      handlePasswordUpdated(false);
    }
  };

  const handlePasswordUpdated = (success = true): void => {
    setVisible(false);
    setOldPassword('');
    setNewPassword('');
    setConfirmPassword('');

    if (success) {
      KitSnack.show(t('blocks:changePassword.passwordUpdated'));
    } else {
      KitSnack.show(
        t('blocks:changePassword.unableToSave'),
        KitSnackDuration.SHORT
      );
    }
  };

  // ---------------------------------------------------------------------------
  // Effects

  useEffect((): void => {
    // Fix for android to auto-focus the input on open
    if (visible) {
      if (Platform.OS === 'android') {
        setTimeout(() => oldPasswordRef.current?.focus(), 150);
      } else {
        oldPasswordRef.current?.focus();
      }
    } else {
      oldPasswordRef.current?.blur();
    }
  }, [visible]);

  return (
    <KitModal visible={visible} setVisible={setVisible}>
      <KeyboardAwareScrollView
        style={{ flex: 1 }}
        contentContainerStyle={styles.scrollContainer}
        keyboardShouldPersistTaps='handled'
        enableOnAndroid={true}
        showsVerticalScrollIndicator={false}
        enableAutomaticScroll={
          Platform.OS === 'android'
            ? Platform.Version < 31 || Platform.Version > 34
            : false // Android 31 - 34
        }
        extraScrollHeight={Platform.OS === 'android' ? insetsBottom : 0}
      >
        <View style={{ margin: Spacing.xl }}>
          <KitText h1>{t('blocks:changePassword.changePassword')}</KitText>
          <KitInput
            inputRef={oldPasswordRef}
            label={t('blocks:changePassword.oldPassword')}
            marginVertical={18}
            onChangeText={setOldPassword}
            secureTextEntry={true}
            testID='OldPassword'
            value={oldPassword}
          />
          <KitInput
            label={t('blocks:changePassword.newPassword')}
            onChangeText={setNewPassword}
            secureTextEntry={true}
            testID='NewPassword'
            value={newPassword}
          />
          <KitInput
            label={t('blocks:changePassword.reenterPassword')}
            marginVertical={18}
            onChangeText={setConfirmPassword}
            secureTextEntry={true}
            testID='ConfirmPaaswrd'
            value={confirmPassword}
          />
          <KitButton
            color={clientBrandColor}
            disabled={!isValidPassword}
            onPress={handleSave}
            testID='SetPasswordButton'
            title={t('blocks:changePassword.setPassword')}
          />
        </View>
      </KeyboardAwareScrollView>
    </KitModal>
  );
};

export default ChangePasswordModal;

const styles = StyleSheet.create({
  scrollContainer: {
    flexGrow: 1,
    justifyContent: 'center',
  },
});
